import { AppTranslationPipe } from '@adapter/i18n/app-translation.pipe';
import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  inject,
  signal
} from '@angular/core';
import { FullLogoutUseCase } from '@usecase/identity/full-logout';
import { GetLogoutUrlUseCase } from '@usecase/identity/get-logout-url';

/**
 * Componente para mostrar un mensaje de acceso denegado
 * cuando un usuario no tiene permiso para ver una página
 */
@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'app-access-denied',
  imports: [AppTranslationPipe],
  template: `
    <div class="page-wrapper">
      <section class="card" role="alert" aria-live="polite">
        <div class="content">
          <div class="status-pill">
            <i class="pi pi-shield"></i>
            <span>403</span>
          </div>
          <h1 class="title">{{ 'security.access-denied' | translate }}</h1>
          <p class="message">
            {{ 'security.access-denied-message' | translate }}
          </p>

          <a
            class="logout-link"
            role="button"
            tabindex="0"
            (click)="onLogout()"
            (keydown.enter)="onLogout()"
            (keydown.space)="onLogout(); $event.preventDefault()"
          >
            {{ 'security.logout' | translate }}
          </a>
        </div>
      </section>
    </div>
  `,
  styles: `
    :host {
      display: flex;
      flex: 1;
      height: 100%;
      overflow: hidden;
      --ad-ink: #0f172a;
      --ad-muted: #475569;
      --ad-primary: #5a8ad7;
      --ad-primary-strong: #2d6df6;
      --ad-surface-soft: #f5f8ff;
    }

    .page-wrapper {
      position: relative;
      display: flex;
      align-items: center;
      justify-content: center;
      flex: 1;
      height: 100%;
      overflow: hidden;
      padding: 2.5rem 1.25rem;
    }

    .card {
      position: relative;
      z-index: 1;
      background: transparent;
      border-radius: 1.25rem;
      box-shadow: none;
      border: none;
      backdrop-filter: none;
      padding: 1rem;
      display: flex;
      justify-content: center;
      text-align: left;
      width: min(100%, 860px);
      transform: translateY(8px);
      animation: reveal 420ms ease-out forwards;
    }

    .content {
      display: flex;
      width: min(100%, 740px);
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: 0.9rem;
      padding: 0.95rem 0.75rem;
    }

    .status-pill {
      align-self: center;
      display: inline-flex;
      align-items: center;
      gap: 0.45rem;
      border-radius: 999px;
      background: var(--ad-surface-soft);
      color: var(--ad-primary-strong);
      border: 1px solid rgba(20, 88, 199, 0.18);
      padding: 0.32rem 0.75rem;
      font-size: 1rem;
      font-weight: 700;
      letter-spacing: 0.08em;
      text-transform: uppercase;
    }

    .status-pill i {
      font-size: 1rem;
    }

    .title {
      margin: 0;
      font-size: clamp(1.45rem, 2.2vw, 1.95rem);
      font-weight: 800;
      line-height: 1.18;
      color: var(--ad-ink);
      letter-spacing: -0.02em;
    }

    .message {
      margin: 0;
      max-width: 58ch;
      font-size: 0.97rem;
      color: var(--ad-muted);
      line-height: 1.6;
      text-align: center;
    }

    .logout-link {
      display: inline-flex;
      align-items: center;
      gap: 0.4rem;
      margin-top: 0.25rem;
      color: var(--ad-primary-strong);
      font-size: 0.92rem;
      font-weight: 500;
      text-decoration: underline;
      text-underline-offset: 0.2rem;
      cursor: pointer;
      transition: color 0.2s ease;
    }

    .logout-link i {
      font-size: 0.82rem;
    }

    .logout-link:hover {
      color: var(--ad-primary);
    }

    .logout-link:focus-visible {
      outline: 3px solid rgba(37, 99, 235, 0.25);
      outline-offset: 2px;
      border-radius: 0.35rem;
    }

    @media (max-width: 520px) {
      .page-wrapper {
        align-items: flex-start;
        padding: 1rem 0.8rem 0.5rem;
      }

      .card {
        text-align: center;
        width: 95%;
        padding: 0.2rem 0.15rem;
        transform: none;
      }

      .content {
        align-items: center;
        gap: 0.75rem;
        padding: 0.65rem 0.35rem 0;
      }

      .status-pill {
        align-self: center;
      }

      .message {
        max-width: 38ch;
        line-height: 1.6;
      }
    }

    @media (prefers-reduced-motion: reduce) {
      .card,
      .logout-link {
        animation: none;
        transition: none;
      }
    }

    @keyframes reveal {
      from {
        opacity: 0;
        transform: translateY(14px) scale(0.985);
      }
      to {
        opacity: 1;
        transform: translateY(0) scale(1);
      }
    }
  `
})
export class AccessDeniedComponent implements OnInit {
  private readonly getLogoutUrlUseCase = inject(GetLogoutUrlUseCase);
  private readonly fullLogoutUseCase = inject(FullLogoutUseCase);

  /** URL de logout obtenida desde el backend */
  private logoutUrl = signal('/');

  ngOnInit(): void {
    this.getLogoutUrlUseCase.execute().subscribe((url) => {
      this.logoutUrl.set(url);
    });
  }

  onLogout(): void {
    this.fullLogoutUseCase.execute(this.logoutUrl());
  }
}
